/* eslint-disable react/jsx-props-no-spreading */

import React, { useContext, useMemo } from "react";
import "react-customize-token-input/dist/react-customize-token-input.css";
import { useNavigate, useParams } from "react-router-dom";
import APIVariableSelect from "../../../Components/APIVariableSelect/APIVariableSelect";
import Input from "../Inputs/Input";
import ConditionOptionsConfig from "./ConditionOptionsConfig";
import Context from "../../../../store/context";
import CommonInput from "../Inputs/CommonInput";
import Errors from "../../../Components/Errors";
import CompoundInput from "../Inputs/CompoundInput";
import InputLabel from "../Inputs/InputLabel";
import labels from "../helpers/labels";
import LogicItemOptions from "../Options/LogicItemOptions";

export default function Condition({ condition, setCondition, index, event }) {
  const context = useContext(Context);
  const navigate = useNavigate();
  const params = useParams();
  const { workflowUUID } = params;

  const pageProps = JSON.parse(
    document.querySelector("#app").dataset.page
  ).props;

  const logicConditions = ConditionOptionsConfig({
    condition,
    context,
    event,
    params: { workflowUUID },
    pageProps,
  });

  const getConditonDefaults = (values) =>
    logicConditions[values[0]]?.inputs?.reduce(
      (p, c) => {
        if (
          c.type === "property" &&
          c.defaultValue !== undefined &&
          c.defaultValue !== null
        ) {
          return { ...p, [c.field]: c.defaultValue[0] };
        }
        if (
          c.type === "option" &&
          c.defaultValue !== undefined &&
          c.defaultValue !== null
        ) {
          return {
            ...p,
            Options: [
              ...p.Options,
              ...c.defaultValue.map((Value) => ({ Type: c.field, Value })),
            ],
          };
        }
        if (
          c.type === "compound" &&
          c.defaultValue !== undefined &&
          c.defaultValue !== null
        ) {
          return {
            ...p,
            Options: [
              ...p.Options,
              ...c.defaultValue.map((Value) =>
                typeof Value === "object"
                  ? {
                      Type: c.field,
                      ValueFrom: "static",
                      Key: c.props.keyValue,
                      ...Value,
                    }
                  : {
                      Type: c.field,
                      Value,
                      ValueFrom: "static",
                      Key: c.props.keyValue,
                    }
              ),
            ],
          };
        }
        return p;
      },
      { Options: [] }
    );

  const selectedConfig = logicConditions[condition.Type];
  const isSummary = !setCondition;
  const parentPath = `/library/logic/builder/${params.projectID}/workflow/${params.workflowUUID}/event/${params.event}/listener/${params.listenerUUID}`;
  const optionsPath = `${parentPath}/condition/${condition.UUID}`;

  const getApiVariableContent = () => {
    if (!setCondition || condition.Type !== "Api Variables") {
      return null;
    }

    return (
      <div className="mb-5 bg-purple-gradient p-2 rounded-2 api-variable">
        <InputLabel>API Variable</InputLabel>
        <div className="p-2">
          <APIVariableSelect
            className="d-inline-block me-1"
            onChange={(option) =>
              setCondition({ ...condition, apiVariable: option.value })
            }
            value={{
              value: condition.apiVariable,
              label: condition.apiVariable || "Select API Variable",
            }}
          />
        </div>
      </div>
    );
  };

  const getOutputVariableContent = () => {
    if (
      !setCondition ||
      !(
        [
          "EmailOrSmsBody",
          "EmailSubject",
          "reply:message",
          "variable:text",
          "validate:phone-number",
          "component:package",
          "workflow:contacts-count",
        ].includes(condition.Type) ||
        (["contact:in"].includes(condition.Type) &&
          condition.Options?.find(
            (option) =>
              option.Type === "where" &&
              ["group-variable", "group"].includes(option.Value)
          ))
      )
    ) {
      return null;
    }

    return (
      <div className="mb-5 bg-purple-gradient p-2 rounded-2">
        <CompoundInput
          label="Output Variable"
          isClearable
          isInputArgumentOnly
          inputArgumentProps={
            ["validate:phone-number"].includes(condition.Type)
              ? { Format: ["voice", "sms", "contact"] }
              : ["contact:in"].includes(condition.Type) &&
                condition.Options?.find(
                  (option) =>
                    option.Type === "where" &&
                    ["group-variable", "group"].includes(option.Value)
                )
              ? { Format: ["group"] }
              : { Format: "short-text" }
          }
          showOperationSelector
          isValidNewOption={(value) => value.trim().length > 0}
          values={[
            {
              ValueFrom: "inputArgument",
              Value: condition.OutputVariableUuid,
              MainThread: condition.OutputVariableUseMainThread,
              Operation: condition.OutputVariableOperation,
            },
          ]}
          onChange={(values) =>
            setCondition({
              ...condition,
              OutputVariableUuid: values[0].Value,
              OutputVariableUseMainThread: values[0].MainThread,
              OutputVariableOperation: values[0].Operation,
            })
          }
          condition={condition}
        />
      </div>
    );
  };

  const typeChangeHandler = (values) => {
    const nextConditionConfig = logicConditions[values[0]];
    const hasSidebarOptions =
      (nextConditionConfig?.inputs?.length || 0) > 0 ||
      values[0] !== "form-validation";

    setCondition({
      UUID: condition.UUID,
      Logical: condition.Logical,
      Type: values[0],
      // set condition defaults
      // ...conditionDefaults({ context })[values[0]],
      ...getConditonDefaults(values),
      Options: [
        ...(values[0] !== "form-validation"
          ? [
              {
                Value: "True",
                Type: "BooleanOperator",
              },
            ]
          : []),
        // ...(conditionDefaults({ context })[values[0]]?.Options ?? []),
        ...(getConditonDefaults(values)?.Options ?? []),
      ],
    });

    if (!isSummary && hasSidebarOptions) {
      navigate(optionsPath);
    }
  };

  const booleanOperatorInput = condition.Type &&
    condition.Type !== "form-validation" && (
      <CommonInput
        className={`${
          !isSummary ? "mb-5 " : ""
        }bg-purple-gradient p-2 rounded-2 `}
        condition={condition}
        setCondition={setCondition}
        input={{
          type: "option",
          field: "BooleanOperator",
          props: {
            label: "Is True/False",
            inputType: "select",
            isValidNewOption: () => false,
            options: [
              { value: "True", label: "is true" },
              { value: "False", label: "is false" },
            ],
          },
        }}
      />
    );
  const apiVariableContent = getApiVariableContent();
  const outputVariableContent = getOutputVariableContent();
  const conditionExtraContent =
    apiVariableContent || booleanOperatorInput || outputVariableContent ? (
      <>
        {apiVariableContent}
        {booleanOperatorInput}
        {outputVariableContent}
      </>
    ) : null;

  return (
    <div>
      <div className="d-flex flex-wrap gap-1">
        {index > 0 && (
          // LOGICAL AND/OR
          <div
            className={`conditions-select-logical ${
              isSummary ? "bg-purple-gradient p-2 rounded-2" : ""
            }`}
          >
            <Input
              isSummary={!setCondition}
              label={isSummary ? "AND / OR" : undefined}
              inputType="select"
              isValidNewOption={() => false}
              options={[
                { value: "AND", label: "AND" },
                { value: "OR", label: "OR" },
              ]}
              value={[condition.Logical || "AND"]}
              onChange={(options) =>
                setCondition({ ...condition, Logical: options[0] })
              }
            />
          </div>
        )}

        {/* CONDITION TYPE */}
        <div
          className={`conditions-select-type ${
            isSummary ? "bg-purple-gradient p-2 rounded-2" : ""
          }`}
          style={!isSummary ? { minWidth: 300 } : undefined}
        >
          <Input
            isSummary={!setCondition}
            label={isSummary ? "Condition type" : undefined}
            autoFocus={!condition.Type}
            inputType="select"
            isSearchable
            isValidNewOption={() => false}
            inputHelp="Select type of condition or type to search"
            options={Object.keys(logicConditions)
              // check if the condition is available in this event
              .filter(
                (cond) => logicConditions[cond].events?.includes(event) ?? true
              )
              .map((opt) => ({
                value: opt,
                label: logicConditions[opt].label,
              }))}
            value={[condition.Type || "Select Type"]}
            onChange={typeChangeHandler}
          />
        </div>

        {isSummary && condition.Type === "Api Variables" && (
          <div className="api-variable me-1">
            <APIVariableSelect
              className="d-inline-block me-1"
              onChange={(option) =>
                setCondition({ ...condition, apiVariable: option.value })
              }
              value={{
                value: condition.apiVariable,
                label: condition.apiVariable || "Select API Variable",
              }}
            />
          </div>
        )}

        {isSummary &&
          // summary inputs
          logicConditions[condition.Type]?.inputs?.map((input, i) => (
            <CommonInput
              isConditionInput
              className={`bg-purple-gradient p-2 rounded-2 ${
                input.className || ""
              }`}
              key={`${condition.UUID}-${input.field}-${i}${input.props?.key}`}
              {...{ input, condition, setCondition }}
            />
          ))}

        {isSummary && booleanOperatorInput}

        {!isSummary && condition.Type && (
          <LogicItemOptions
            item={condition}
            itemType="condition"
            title={`Condition options - ${
              selectedConfig?.label || labels[condition.Type] || condition.Type
            }`}
            config={selectedConfig}
            isOpen={params.conditionUUID === condition.UUID}
            optionsPath={optionsPath}
            parentPath={parentPath}
            isConditionInput
            errorFilter={(error) => error.condition?.UUID === condition.UUID}
            setItem={setCondition}
            extraContent={conditionExtraContent}
          />
        )}
      </div>
      <div className="text-danger">
        <Errors
          filterFn={(err) => err.condition?.UUID === condition.UUID}
          linkDisabled
        />
      </div>
    </div>
  );
}
